// Canonical admin navigation data — SINGLE SOURCE OF TRUTH for the admin menu
// (Critical Rule #8). Plain module (NOT 'use client') so it is safe to import
// from both server components (personal-account-navigation.config, header nav)
// and client components (sidebar, mobile, command palette, favorites).
//
// admin-tab-context.tsx re-exports everything here and adds the React context.

import {
  Banknote,
  Bell,
  Building,
  Building2,
  Calendar,
  CalendarPlus,
  Clapperboard,
  ClipboardList,
  Cog,
  CreditCard,
  DownloadCloud,
  FileBarChart,
  FileText,
  Gavel,
  Gift,
  Handshake,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  LucideIcon,
  Mail,
  MapPin,
  Megaphone,
  Receipt,
  Send,
  Store,
  Tag,
  Theater,
  Trophy,
  Users,
  UsersRound,
} from 'lucide-react';

import { ADMIN_MODULE_TABS } from '~/admin/_components/admin-module-tabs-registry';

// Navigation item type
export interface AdminNavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  end?: boolean;
  /** Mobile tab-bar rank when acting as Admin; see `NavItemDef.primary`. */
  primary?: number;
}

// Navigation group type
export interface AdminNavGroup {
  label: string;
  icon: LucideIcon;
  items: AdminNavItem[];
}

// All admin navigation groups.
//
// SINGLE SOURCE OF TRUTH for the admin menu (Critical Rule #8 — single edit
// surface). The desktop sidebar (super-admin-tabbed-navigation), mobile nav
// (home-mobile-navigation), command palette, and favorites all import from
// here. Add a new admin page here ONCE and it appears in every surface.
//
// 2026-07-05 nav IA (docs/architecture/navigation-ia-proposal-2026-07-05.md,
// Phase B): 6 groups, ≤7 items each. Module pages absorb their facets as L2
// tab bars (apps/web/app/admin/_components/admin-module-tabs.tsx) — the
// absorbed routes keep working, only their sidebar entries moved. Cast Grid
// (/admin/events/cast-grid) stays unsurfaced (BAL-318).
export const adminNavigationGroups: AdminNavGroup[] = [
  {
    label: 'Operations',
    icon: Calendar,
    items: [
      { label: 'Dashboard', path: '/admin', icon: LayoutDashboard, end: true },
      {
        label: 'Events',
        path: '/admin/events',
        icon: CalendarPlus,
        primary: 2,
      },
      // Productions module tabs: Repertoire, Casting Pools.
      { label: 'Productions', path: '/admin/productions', icon: Theater },
      // Venues left the Productions tab bar (2026-09-17): admins creating
      // events could not find where to add a venue.
      { label: 'Venues', path: '/admin/venues', icon: MapPin },
      // Cast Sheet is a mobile tab when acting as Admin (navigation audit
      // 2026-10-02: a super admin could not find cast management on a phone).
      {
        label: 'Cast Sheet',
        path: '/admin/castsheet',
        icon: FileBarChart,
        primary: 3,
      },
      { label: 'Operations', path: '/admin/operations', icon: ClipboardList },
    ],
  },
  {
    label: 'Talent',
    icon: UsersRound,
    items: [
      // Artists module tabs: Map, Analytics, Fever Ratings, ID Verification.
      { label: 'Artists', path: '/admin/artists', icon: UsersRound },
      // Casting module tabs: Hire Orders, Dance Assistants, Coaching.
      // (Hire ORDERS are client/show-contract generated — a distinct domain
      // from Hire Offers below.)
      { label: 'Casting', path: '/admin/hire-orders', icon: Clapperboard },
      // Hire OFFERS = proposals for hire made to a dancer. Standalone module,
      // no sub-tabs.
      { label: 'Hire Offers', path: '/admin/offers', icon: Send },
      // Competitions module tabs: Offers (competition scholarship offers).
      { label: 'Competitions', path: '/admin/competitions', icon: Trophy },
      // Audition Imports module tabs: Sources, Imports (external-audition
      // ingestion pipeline).
      {
        label: 'Audition Imports',
        path: '/admin/audition-imports',
        icon: DownloadCloud,
      },
    ],
  },
  {
    label: 'Growth',
    icon: Megaphone,
    items: [
      // Automation module tabs: Campaigns · A/B Tests · Analytics.
      { label: 'Campaigns', path: '/admin/automation/campaigns', icon: Bell },
      // Communications module tabs: WhatsApp Timeline.
      { label: 'Communications', path: '/admin/communications', icon: Mail },
      { label: 'Shortlinks', path: '/admin/shortlinks', icon: Link2 },
      { label: 'Referrals', path: '/admin/referrals', icon: Gift },
      { label: 'Affiliate', path: '/admin/affiliate', icon: Handshake },
    ],
  },
  {
    label: 'Commerce',
    icon: Store,
    items: [
      { label: 'Shops', path: '/admin/shops', icon: Store },
      { label: 'Keywords', path: '/admin/marketplace/keywords', icon: Tag },
      {
        label: 'Subscriptions',
        path: '/admin/subscriptions',
        icon: CreditCard,
      },
    ],
  },
  {
    label: 'Finance',
    icon: Receipt,
    items: [
      { label: 'Dashboard', path: '/admin/finance', icon: LayoutDashboard },
      // Invoices module tabs: Artist, Client, Monthly, Analytics.
      {
        label: 'Invoices',
        path: '/admin/finance/artist-invoices',
        icon: FileText,
      },
      {
        label: 'Reimbursements',
        path: '/admin/finance/reimbursements',
        icon: Receipt,
      },
      { label: 'Payments', path: '/admin/finance/payments', icon: Banknote },
      {
        label: 'Client Billing',
        path: '/admin/finance/clients',
        icon: Building2,
      },
      // Tipalti notices from Fever that need a person (proposals, unmatched).
      { label: 'Inbound emails', path: '/admin/inbound-emails', icon: Inbox },
    ],
  },
  {
    label: 'Platform',
    icon: Cog,
    items: [
      // Partners module tabs: Clients, Agencies, Organizations, Accounts.
      { label: 'Partners', path: '/admin/clients', icon: Handshake },
      // Organizations is also a Partners tab, but admins look for it by name
      // (served on both brands since #2917, route-brand.config.ts).
      {
        label: 'Organizations',
        path: '/admin/organizations',
        icon: Building,
      },
      // Users & Roles module tabs: Users, Roles.
      { label: 'Users & Roles', path: '/admin/users', icon: Users },
      // Settings module tabs: Categories, Automations, Data Sync, Maintenance.
      { label: 'Settings', path: '/admin/settings', icon: Cog },
      // The /admin/support inbox was retired (support→Ballee-org, Phase 4b);
      // the module now lands on its Flow-linked Feedback + Issues tabs.
      { label: 'Support', path: '/admin/feedback', icon: LifeBuoy },
      // Compliance module tabs: Legal, Audit.
      {
        label: 'Compliance',
        path: '/admin/legal-compliance',
        icon: Gavel,
      },
    ],
  },
];

// Flat list of every admin nav item, derived from the groups above.
export const adminNavItems: AdminNavItem[] = adminNavigationGroups.flatMap(
  (group) => group.items,
);

// Path -> item lookup, derived from the groups above. Used by surfaces that key
// page metadata by route (mobile nav breadcrumbs, favorites).
export const adminNavItemsByPath: Record<string, AdminNavItem> =
  Object.fromEntries(adminNavItems.map((item) => [item.path, item]));

/** One entry of the admin command palette's search index. */
export interface AdminSearchItem extends AdminNavItem {
  /** Sidebar group the entry is listed under. */
  group: string;
  /** For a module-tab page: the sidebar module that hosts the tab. */
  module?: string;
}

/** A command-palette group: the sidebar group's items, then the tab-only
 *  pages of the modules it hosts. */
export interface AdminSearchGroup {
  label: string;
  items: AdminSearchItem[];
}

/**
 * The admin search index: every sidebar item PLUS every page reachable only
 * as a module tab (admin-module-tabs-registry.ts), so typing "organizations"
 * finds `/admin/organizations` even though it lives under Partners. Derived
 * from the two registries, never listed by hand (navigation audit 2026-10-02,
 * F9: 28 tab-only pages were unfindable by name).
 */
export const adminSearchGroups: AdminSearchGroup[] = (() => {
  const sidebarPaths = new Set(adminNavItems.map((item) => item.path));
  const tabOnlyByGroup = new Map<string, AdminSearchItem[]>();
  const indexed = new Set<string>();

  for (const tabs of Object.values(ADMIN_MODULE_TABS)) {
    // The host is the sidebar item a tab points at, else the deepest one
    // whose path is a parent of a tab: the settings tabs are sub-pages of
    // the `/admin/settings` sidebar item, which only redirects, and every
    // admin URL is also under the `/admin` Dashboard.
    const host =
      tabs
        .map((tab) => adminNavItemsByPath[tab.href])
        .find((item): item is AdminNavItem => item !== undefined) ??
      adminNavItems
        .filter((item) =>
          tabs.some((tab) => tab.href.startsWith(`${item.path}/`)),
        )
        .reduce<
          AdminNavItem | undefined
        >((deepest, item) => (!deepest || item.path.length > deepest.path.length ? item : deepest), undefined);
    if (!host) continue;
    const group = adminNavigationGroups.find((g) => g.items.includes(host));
    if (!group) continue;

    for (const tab of tabs) {
      if (sidebarPaths.has(tab.href) || indexed.has(tab.href)) continue;
      indexed.add(tab.href);
      const list = tabOnlyByGroup.get(group.label) ?? [];
      list.push({
        label: tab.label,
        path: tab.href,
        icon: host.icon,
        group: group.label,
        module: host.label,
      });
      tabOnlyByGroup.set(group.label, list);
    }
  }

  return adminNavigationGroups.map((group) => ({
    label: group.label,
    items: [
      ...group.items.map((item) => ({ ...item, group: group.label })),
      ...(tabOnlyByGroup.get(group.label) ?? []),
    ],
  }));
})();

/** Flat view of {@link adminSearchGroups}. */
export const adminSearchItems: AdminSearchItem[] = adminSearchGroups.flatMap(
  (group) => group.items,
);

// Extra search synonyms per route, surfaced in the command palette (not rendered
// in the sidebar). Keyed by path so the canonical group list stays terse.
// Entries for routes that are no longer sidebar items (they moved into module
// tab bars, 2026-07-05) are intentionally kept so palette search history and
// future re-indexing keep their synonyms.
export const adminNavKeywordsByPath: Record<string, string[]> = {
  '/admin': ['home', 'overview'],
  '/admin/castsheet': ['assignments', 'schedule'],
  '/admin/events': ['calendar', 'shows', 'performances'],
  '/admin/productions': ['shows', 'series'],
  '/admin/operations': ['logistics', 'incidents'],
  '/admin/repertoire': ['pieces', 'choreography'],
  '/admin/venues': ['locations', 'theaters'],
  '/admin/casting-pools': ['casting', 'shortlist'],
  '/admin/artists': ['performers', 'artists', 'talent'],
  '/admin/artists/map': ['map', 'geography', 'location'],
  '/admin/artist-analytics': ['stats', 'metrics', 'performance'],
  '/admin/fever-ratings': ['reviews', 'scores'],
  '/admin/coaching': ['coaches', 'sessions', 'training'],
  '/admin/dance-assistants': ['assistants', 'helpers'],
  '/admin/hire-orders': ['contracts', 'agreements', 'hire orders'],
  '/admin/legal-compliance/identity-verifications': [
    'identity',
    'kyc',
    'documents',
  ],
  '/admin/competitions': ['gdo', 'contests', 'tournaments'],
  '/admin/offers': ['hire', 'auditions', 'scholarships'],
  '/admin/audition-sources': ['monitor', 'scraper', 'external', 'import'],
  '/admin/audition-imports': ['review', 'queue', 'extracted', 'external'],
  '/admin/automation/campaigns': ['messages', 'broadcasts', 'sequences'],
  '/admin/communications': ['email', 'whatsapp', 'broadcast', 'newsletter'],
  '/admin/shortlinks': ['links', 'clicks', 'tracking', 'utm', 'redirect'],
  '/admin/communication-timeline': ['whatsapp', 'timeline', 'scheduled'],
  '/admin/automation/ab-tests': ['experiments', 'testing'],
  '/admin/automation/analytics': ['stats', 'metrics'],
  '/admin/referrals': ['invites', 'recruit'],
  '/admin/affiliate': [
    'brand',
    'partnerships',
    'commission',
    'recommendations',
  ],
  '/admin/shops': ['marketplace', 'storefront', 'sellers'],
  '/admin/marketplace/keywords': ['search', 'tags', 'seo', 'marketplace'],
  '/admin/subscriptions': ['billing', 'plans', 'recurring'],
  '/admin/finance': ['money', 'overview'],
  '/admin/finance/clients': ['billing', 'client billing'],
  '/admin/finance/artist-invoices': ['payments', 'billing', 'artist'],
  '/admin/finance/client-invoices': ['billing', 'ar'],
  '/admin/finance/reimbursements': ['expenses', 'claims'],
  '/admin/finance/payments': ['payouts', 'transfers', 'tipalti'],
  '/admin/inbound-emails': ['tipalti', 'payments', 'email', 'disputes'],
  '/admin/finance/invoices/analytics': [
    'invoice',
    'stats',
    'metrics',
    'charts',
  ],
  '/admin/finance/invoices/monthly': [
    'invoice',
    'monthly',
    'review',
    'clients',
  ],
  '/admin/clients': ['companies', 'organizations', 'clients', 'partners'],
  '/admin/agencies': ['agency', 'roster', 'partners'],
  '/admin/organizations': ['orgs', 'companies', 'partners'],
  '/admin/accounts': ['workspaces', 'teams'],
  '/admin/feedback': ['reviews', 'comments', 'support'],
  '/admin/issues': ['bugs', 'problems'],
  '/admin/legal-compliance': ['legal', 'compliance', 'regulations'],
  '/admin/audit': ['log', 'history', 'security'],
  '/admin/users': ['people', 'members'],
  '/admin/roles': ['permissions', 'access'],
  '/admin/categories': ['taxonomy', 'tags'],
  '/admin/automations': ['workflows', 'triggers'],
  '/admin/sync': ['meteor', 'import'],
  '/admin/maintenance': ['cleanup', 'tools'],
  '/admin/settings': ['config', 'preferences'],
};
